import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EntityTypeEnum, ExpeditionCoordinate, SupplierNotificationTypeEnum } from '@api/ringil-api.v2.tsx';
import { resolveExpeditionTarget } from '@hooks/expeditionTarget.ts';
import { CarrierReferrer } from '@app/pages/customer/carriers/carrier-detail/CarrierDetail.tsx';
import { JobReferrer } from '@components/ringil3/Features/Job/jobReferrer.ts';

interface Params {
  applicationParams?: { [key: string]: string };
  queryParams?: { [key: string]: string };
}

interface URLInfo {
  pattern: string;
  applicationParams?: { [key: string]: string };
  queryParams?: { [key: string]: string };
}

const urlFunctions = {
  createCustomerNotifications: () => {
    return {
      pattern: '/notifications',
      queryParams: {},
      applicationParams: {},
    };
  },
  createEmailHistoryList: () => {
    return {
      pattern: '/email-history',
      queryParams: {},
      applicationParams: {},
    };
  },
  createCompanyNotificationList: () => {
    return {
      pattern: '/company-notifications',
      queryParams: {},
      applicationParams: {},
    };
  },
  createSupplierManifestDetail: (supplierManifestId: string): URLInfo => {
    return {
      pattern: '/supplier-manifest/{supplierManifestId}',
      queryParams: {},
      applicationParams: { supplierManifestId: supplierManifestId },
    };
  },
  createSupplierNotificationDetail: (supplierNotificationId: string, snType: SupplierNotificationTypeEnum): URLInfo => {
    return {
      pattern: '/supplier-notifications/{supplierNotificationId}',
      queryParams: { snType: snType },
      applicationParams: { supplierNotificationId: supplierNotificationId },
    };
  },
  createShipmentDetail: (shipmentAppId: string): URLInfo => {
    return {
      pattern: '/shipments/{shipmentAppId}',
      queryParams: {},
      applicationParams: { shipmentAppId: shipmentAppId },
    };
  },
  createCustomerGroupDemandDetail: (groupDemandId: string): URLInfo => {
    return {
      pattern: '/multi-demands/{groupDemandId}',
      queryParams: {},
      applicationParams: { groupDemandId: groupDemandId },
    };
  },
  createCustomerExpeditionList: (queryParams?: { [key: string]: any }): URLInfo => {
    return {
      pattern: '/customer/expeditions',
      queryParams: queryParams,
      applicationParams: {},
    };
  },
  createExpedition: (): URLInfo => {
    return {
      pattern: '/expedition/create',
      queryParams: {},
      applicationParams: {},
    };
  },
  /** The create form, pre-filled by merging the given shipments into one. */
  createMergedExpedition: (expeditionIds: string[]): URLInfo => {
    return {
      pattern: '/expedition/create',
      queryParams: { mergeExpeditionIds: expeditionIds.join(',') },
      applicationParams: {},
    };
  },
  /** The create form, pre-filled by merging the given supplier notifications into one shipment. */
  createMergedExpeditionFromSNs: (supplierNotificationIds: string[]): URLInfo => {
    return {
      pattern: '/expedition/create',
      queryParams: { mergeSupplierNotificationIds: supplierNotificationIds.join(',') },
      applicationParams: {},
    };
  },
  /** The group itself travels in session storage (routeConsolidationStorage.ts); the URL only names it. */
  createExpeditionFromConsolidation: (consolidationRequestId: string, consolidationGroupId: number): URLInfo => {
    return {
      pattern: '/expedition/create',
      queryParams: { consolidationRequestId, consolidationGroupId: String(consolidationGroupId) },
      applicationParams: {},
    };
  },
  createExpeditionFromEntity: (entityId: string, entityType: EntityTypeEnum, queryParams?: { [key: string]: any }): URLInfo => {
    return {
      pattern: '/expedition/create/{entityId}',
      queryParams: { ...queryParams, entityType },
      applicationParams: { entityId },
    };
  },
  createNewExpeditionImport: (): URLInfo => {
    return {
      pattern: '/expedition/import',
      queryParams: {},
      applicationParams: {},
    };
  },
  createMyCarrierList: (): URLInfo => {
    return {
      pattern: '/customer/carriers',
      queryParams: {},
      applicationParams: {},
    };
  },
  createCustomerExpeditionTemplates: () => {
    return {
      pattern: '/customer/routes',
      queryParams: {},
      applicationParams: {},
    };
  },
  createCustomerExpeditionTemplate: () => {
    return {
      pattern: '/customer/route',
      queryParams: {},
      applicationParams: {},
    };
  },
  createCarrierGroups: () => {
    return {
      pattern: '/customer/carrier-groups',
      queryParams: {},
      applicationParams: {},
    };
  },
  createCarrierGroupDetail: (carrierGroupId: string) => {
    return {
      pattern: '/customer/carrier-group/{carrierGroupId}',
      queryParams: {},
      applicationParams: { carrierGroupId },
    };
  },
  createJobDetail: (jobId: string, referrer?: JobReferrer) => {
    return {
      pattern: '/customer/job/{jobId}',
      queryParams: { jobReferrer: referrer ?? JobReferrer.JOBS },
      applicationParams: { jobId },
    };
  },
  createJobList: () => {
    return {
      pattern: '/customer/jobs',
      queryParams: {},
      applicationParams: {},
    };
  },
  createPartnerList: () => {
    return {
      pattern: '/customer/partners',
      queryParams: {},
      applicationParams: {},
    };
  },
  createCarrierCatalogList: () => {
    return {
      pattern: '/customer/carrier-catalog',
      queryParams: {},
      applicationParams: {},
    };
  },
  createSupplierNotificationList: (queryParams?: { [key: string]: any }) => {
    return {
      pattern: '/supplier-notifications',
      queryParams: queryParams,
      applicationParams: {},
    };
  },
  createMultiDemansList: () => {
    return {
      pattern: '/multi-demands',
      queryParams: {},
      applicationParams: {},
    };
  },
  createShipmentList: (queryParams?: { [key: string]: any }) => {
    return {
      pattern: '/shipments',
      queryParams: queryParams,
      applicationParams: {},
    };
  },
  createLocationsList: () => {
    return {
      pattern: '/customer/locations',
      queryParams: {},
      applicationParams: {},
    };
  },
  createLocationDetail: (locationId: string) => {
    return {
      pattern: '/customer/locations/{locationId}',
      queryParams: {},
      applicationParams: { locationId },
    };
  },
  createRampDetail: (locationId: string, rampId: string) => {
    return {
      pattern: '/customer/locations/{locationId}/ramp/{rampId}',
      queryParams: {},
      applicationParams: { locationId, rampId },
    };
  },
  createExceptionList: (queryParams?: { [key: string]: any }) => {
    return {
      pattern: '/customer/exceptions',
      queryParams: queryParams,
      applicationParams: {},
    };
  },
  createCarrierDetail: (carrierId: string, carrierReferrer?: CarrierReferrer) => {
    return {
      pattern: '/customer/carrier/{carrierId}',
      queryParams: { carrierReferrer: carrierReferrer ? carrierReferrer : CarrierReferrer.CARRIERS },
      applicationParams: { carrierId },
    };
  },
  createPartnerDetail: (partnerId: string) => {
    return {
      pattern: '/customer/partner/{partnerId}',
      queryParams: {},
      applicationParams: { partnerId },
    };
  },
  createReportUrl: (reportId: string) => {
    return {
      pattern: '/reports/{reportId}',
      queryParams: {},
      applicationParams: { reportId },
    };
  },
  createTimeslotCalendar: () => {
    return {
      pattern: '/timeslots/calendar',
      queryParams: {},
      applicationParams: {},
    };
  },
  createReservationList: (queryParams?: { [key: string]: any }) => {
    return {
      pattern: '/timeslots/reservations',
      queryParams: queryParams,
      applicationParams: {},
    };
  },
  createLocalityDetail: (localitylId: string) => {
    return {
      pattern: '/customer/locations/{localitylId}',
      queryParams: {},
      applicationParams: { localitylId },
    };
  },
  createProcessShipment: (fromShipmentId: string) => {
    return {
      pattern: '/supplier-notifications/process-shipment',
      queryParams: { fromShipmentId },
      applicationParams: {},
    };
  },
  createCarrierShipmentDetail: (companyId: string, applicationId: string) => {
    return {
      pattern: '/carrier/shipment/{companyId}-{applicationId}',
      queryParams: {},
      applicationParams: { companyId, applicationId },
    };
  },
  createCarrierDemandDetailByQuoteId: (quoteId: string) => {
    return {
      pattern: '/carrier/demand/{quoteId}',
      queryParams: {},
      applicationParams: { quoteId },
    };
  },
  createCustomerExpeditionTemplateDetail: (expeditionTemplateId: string) => {
    return {
      pattern: '/customer/route/{expeditionTemplateId}',
      queryParams: {},
      applicationParams: { expeditionTemplateId },
    };
  },
  createCarrierMultiDemandDetail: (groupDemandId: string) => {
    return {
      pattern: '/carrier/multi-demands/{groupDemandId}',
      queryParams: {},
      applicationParams: { groupDemandId },
    };
  },
  createMultiDemanDetail: (groupDemandId: string) => {
    return {
      pattern: '/multi-demands/{groupDemandId}',
      queryParams: {},
      applicationParams: { groupDemandId },
    };
  },
  createReservationDetail: (reservationId: string) => {
    return {
      pattern: '/timeslots/{reservationId}',
      queryParams: {},
      applicationParams: { reservationId },
    };
  },
  createCarrierMultiDemandsList: (queryParams?: { [key: string]: any }) => {
    return {
      pattern: '/carrier/multi-demands',
      queryParams: queryParams,
      applicationParams: {},
    };
  },
  createReservation: () => {
    return {
      pattern: '/timeslots/create-reservation',
      queryParams: {},
      applicationParams: {},
    };
  },
  createCarrierDemandsList: (queryParams?: { [key: string]: any }) => {
    return {
      pattern: '/carrier/demands',
      queryParams: queryParams,
      applicationParams: {},
    };
  },
  createCarrierShipmentsList: (queryParams?: { [key: string]: any }) => {
    return {
      pattern: '/carrier/shipments',
      queryParams: queryParams,
      applicationParams: {},
    };
  },
  createMyProfile: () => {
    return {
      pattern: '/my-profile',
      queryParams: {},
      applicationParams: {},
    };
  },
  createCompanyProfile: () => {
    return {
      pattern: '/company-profile',
      queryParams: {},
      applicationParams: {},
    };
  },
  createIhneritedCompanies: () => {
    return {
      pattern: '/inherited-companies',
      queryParams: {},
      applicationParams: {},
    };
  },
  partnersUrlInfo: (tab?: string): URLInfo => {
    return {
      pattern: '/settings/partners',
      queryParams: tab ? { tab } : {},
      applicationParams: {},
    };
  },
  partnerDetailUrlInfo: (partnerId: string): URLInfo => {
    return {
      pattern: '/settings/partners/{partnerId}',
      queryParams: {},
      applicationParams: { partnerId },
    };
  },
  volumetricUrlInfo: () => {
    return {
      pattern: '/settings/volumetric',
      queryParams: {},
      applicationParams: {},
    };
  },
  calendarLayoutUrlInfo: () => {
    return {
      pattern: '/settings/calendar-layout',
      queryParams: {},
      applicationParams: {},
    };
  },
  /** `ruleId` opens that rule straight in the editor — the screen reads it from `?rule=`. */
  aiDispatcherUrlInfo: (ruleId?: string) => {
    return {
      pattern: '/settings/ai-dispatcher',
      queryParams: { rule: ruleId },
      applicationParams: {},
    };
  },
  aiDispatcherActivityUrlInfo: () => {
    return {
      pattern: '/settings/ai-dispatcher/activity',
      queryParams: {},
      applicationParams: {},
    };
  },
  calendarLayoutModel: (modelId: string) => {
    return {
      pattern: '/settings/calendar-layout/{modelId}',
      queryParams: {},
      applicationParams: { modelId },
    };
  },
  adminCalendarLayout: () => {
    return {
      pattern: '/admin/tools/calendar-card-designer',
      queryParams: {},
      applicationParams: {},
    };
  },
  adminCalendarLayoutModel: (modelId: string) => {
    return {
      pattern: '/admin/tools/calendar-card-designer/{modelId}',
      queryParams: {},
      applicationParams: { modelId },
    };
  },
  createShipmentCreate: () => {
    return {
      pattern: '/shipments/create-shipment',
      queryParams: {},
      applicationParams: {},
    };
  },
  createSupplierNotificationFromShipment: (fromShipmentId: string) => {
    return {
      pattern: '/supplier-notifications/process-shipment',
      queryParams: { fromShipmentId },
      applicationParams: {},
    };
  },
  createSupplierNotificationCreate: () => {
    return {
      pattern: '/supplier-notifications/create-supplier-notification',
      queryParams: {},
      applicationParams: {},
    };
  },
  createSupplierNotificationDapCreate: () => {
    return {
      pattern: '/supplier-notifications/create-supplier-notification-dap',
      queryParams: {},
      applicationParams: {},
    };
  },
  createDuplicateSupplierNotification: (fromSupplierNotificationId: string) => {
    return {
      pattern: '/supplier-notifications/duplicate-supplier-notification',
      queryParams: { fromSupplierNotificationId },
      applicationParams: {},
    };
  },
  createHomePage: () => {
    return {
      pattern: '/',
      queryParams: {},
      applicationParams: {},
    };
  },
  createExpeditionDetailV2: (expeditionId: string, openMessages?: boolean, selectedCarrierId?: string) => {
    return {
      pattern: '/expedition/{expeditionId}',
      queryParams: {
        openMessages: String(openMessages ?? false),
        ...(selectedCarrierId ? { selectedCarrierId } : {}),
      },
      applicationParams: { expeditionId },
    };
  },
  /** Customer or carrier expedition detail, whichever side `coords` says the user is on — see {@link resolveExpeditionTarget}. */
  createExpeditionDetailForUser: (expeditionId: string, coords?: ExpeditionCoordinate, openMessages?: boolean, selectedCarrierId?: string) => {
    const target = resolveExpeditionTarget(expeditionId, coords);
    return target.side === 'carrier'
      ? urlFunctions.createCarrierShipmentDetail(target.customerId, target.applicationId)
      : urlFunctions.createExpeditionDetailV2(target.expeditionId, openMessages, selectedCarrierId);
  },
  createAdminTimeslotSettings: (companyId: string) => {
    return {
      pattern: '/admin/module-settings/timeslot/{companyId}',
      queryParams: {},
      applicationParams: { companyId },
    };
  },
  createAdminSupplierNotificationSettings: (companyId: string) => {
    return {
      pattern: '/admin/module-settings/supplier-notification/{companyId}',
      queryParams: {},
      applicationParams: { companyId },
    };
  },
  createAdminExpeditionSettings: (companyId: string) => {
    return {
      pattern: '/admin/module-settings/expedition/{companyId}',
      queryParams: {},
      applicationParams: { companyId },
    };
  },
  createAdminAiDispatcherSettings: (companyId: string) => {
    return {
      pattern: '/admin/module-settings/ai-dispatcher/{companyId}',
      queryParams: {},
      applicationParams: { companyId },
    };
  },
  createAdminAiDispatcherActivity: (companyId: string) => {
    return {
      pattern: '/admin/module-settings/ai-dispatcher/{companyId}/activity',
      queryParams: {},
      applicationParams: { companyId },
    };
  },
  createCamsDetail: () => {
    return {
      pattern: '/timeslots/cams',
      queryParams: {},
      applicationParams: {},
    };
  },
  createCamsSettings: () => {
    return {
      pattern: '/timeslots/cams-settings',
      queryParams: {},
      applicationParams: {},
    };
  },
  createVisitorList: () => {
    return {
      pattern: '/timeslots/visits',
      queryParams: {},
      applicationParams: {},
    };
  },
  createVisitDetail: (visitId: string) => {
    return {
      pattern: '/timeslots/visits/{visitId}',
      queryParams: {},
      applicationParams: { visitId },
    };
  },
};

const useNavigation = () => {
  const router = useNavigate();
  const { i18n } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();
  const runAs = searchParams.get('runAs');

  const navigate = (urlInfo: URLInfo) => {
    const url = interpolate(urlInfo.pattern, {
      applicationParams: urlInfo.applicationParams,
      queryParams: { ...urlInfo.queryParams, runAs: runAs },
    });
    router(url);
  };

  const createNavigationLink = (urlInfo: URLInfo) => {
    const url = interpolate(urlInfo.pattern, {
      applicationParams: urlInfo.applicationParams,
      queryParams: { ...urlInfo.queryParams, runAs: runAs },
    });
    return url;
  };

  const interpolate = (val: string, params: Params) => {
    const queryParams = buildParams(params.queryParams);

    if (Object.keys(params.applicationParams || {}).length == 0) return queryParams.length > 0 ? `/${i18n.language}${val}?${queryParams}` : `/${i18n.language}${val}`;
    const interpolatedString = Object.keys(params.applicationParams).reduce((res, item) => {
      return res.replace(`{${item}}`, params.applicationParams[item]);
    }, val);

    return `/${i18n.language}${interpolatedString}?${queryParams}`;
  };

  return {
    navigate,
    createNavigationLink,
    urlFunctions,
  };
};

const buildParams = (data: object) => {
  const params = new URLSearchParams();

  Object.entries(data).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach(value => params.append(key, value.toString()));
    } else {
      if (value) params.append(key, value?.toString());
    }
  });

  return params.toString();
};
export default useNavigation;
